/**
 * ExportValidationDialog — Modal showing pre-export type compatibility results.
 *
 * Displays per-table validation status with expandable per-column issues.
 * Allows "Export Anyway" for warnings-only or "Fix Issues" for errors.
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircleOutline as PassIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as ErrorIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CloudUpload as ExportIcon,
} from '@mui/icons-material';
import type { TableValidationResult } from '../../services/connectorApi';
import type { OutputQualitySummary } from './hooks/useExportPipeline';
import {
  Build as CorrectionIcon,
} from '@mui/icons-material';

/** Validation result from the output_validator for hierarchical assembly output. */
export interface AssemblyValidationResult {
  passed: boolean;
  results: Array<{
    name: string;
    passed: boolean;
    errors: string[];
    error_count: number;
  }>;
  total_errors: number;
}

interface ExportValidationDialogProps {
  open: boolean;
  onClose: () => void;
  validationResults: TableValidationResult[];
  connectorName: string;
  onExportAnyway: () => void;
  qualitySummaries?: Map<string, OutputQualitySummary>;
  /** Validation results for assembled hierarchical output (XML/JSON). */
  assemblyValidation?: AssemblyValidationResult | null;
}

const statusConfig = {
  pass: { icon: PassIcon, color: '#059669', label: 'Pass', bg: '#059669' },
  warn: { icon: WarnIcon, color: '#d97706', label: 'Warnings', bg: '#d97706' },
  fail: { icon: ErrorIcon, color: '#dc2626', label: 'Errors', bg: '#dc2626' },
} as const;

const severityColors = {
  ok: '#059669',
  warning: '#d97706',
  error: '#dc2626',
} as const;

const ExportValidationDialog: React.FC<ExportValidationDialogProps> = ({
  open,
  onClose,
  validationResults,
  connectorName,
  onExportAnyway,
  qualitySummaries,
  assemblyValidation,
}) => {
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());

  const hasErrors = validationResults.some((r) => r.status === 'fail') || (assemblyValidation?.passed === false);
  const hasWarnings = validationResults.some((r) => r.status === 'warn');
  const allPass = !hasErrors && !hasWarnings;

  const toggleTable = (table: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev);
      if (next.has(table)) next.delete(table);
      else next.add(table);
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1, fontSize: 16 }}>
          Export Validation
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: 12 }}>
          Checking compatibility with <strong>{connectorName}</strong>
        </Typography>

        {/* Summary banner */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            mb: 2,
            borderRadius: 1.5,
            bgcolor: allPass
              ? alpha('#059669', 0.06)
              : hasErrors
                ? alpha('#dc2626', 0.06)
                : alpha('#d97706', 0.06),
            border: `1px solid ${allPass ? alpha('#059669', 0.2) : hasErrors ? alpha('#dc2626', 0.2) : alpha('#d97706', 0.2)}`,
          }}
        >
          {allPass && <PassIcon sx={{ color: '#059669' }} />}
          {hasErrors && <ErrorIcon sx={{ color: '#dc2626' }} />}
          {!allPass && !hasErrors && <WarnIcon sx={{ color: '#d97706' }} />}
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
            {allPass
              ? 'All tables pass validation'
              : hasErrors
                ? `${validationResults.filter((r) => r.status === 'fail').length} table(s) have errors`
                : `${validationResults.filter((r) => r.status === 'warn').length} table(s) have warnings`}
          </Typography>
        </Box>

        {/* Quality summary section */}
        {qualitySummaries && qualitySummaries.size > 0 && (() => {
          const vals = Array.from(qualitySummaries.values());
          const totalFailed = vals.reduce((n, s) => n + s.failedChecks.length, 0);
          const totalCorrections = vals.reduce((n, s) => n + s.correctionCount, 0);
          const totalWarnings = vals.reduce((n, s) => n + s.warnings.length, 0);
          if (totalFailed === 0 && totalCorrections === 0 && totalWarnings === 0) return null;
          return (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, color: '#475569', mb: 0.5, display: 'block' }}>
                Data Quality
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: totalFailed > 0 ? alpha('#dc2626', 0.06) : alpha('#d97706', 0.06),
                  border: `1px solid ${totalFailed > 0 ? alpha('#dc2626', 0.2) : alpha('#d97706', 0.2)}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                {totalFailed > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <ErrorIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                    <Typography variant="caption" sx={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>
                      {totalFailed} quality check{totalFailed !== 1 ? 's' : ''} failed
                    </Typography>
                  </Box>
                )}
                {totalWarnings > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <WarnIcon sx={{ fontSize: 14, color: '#d97706' }} />
                    <Typography variant="caption" sx={{ fontSize: 11, color: '#d97706', fontWeight: 600 }}>
                      {totalWarnings} quality warning{totalWarnings !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                )}
                {totalCorrections > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CorrectionIcon sx={{ fontSize: 14, color: '#2563eb' }} />
                    <Typography variant="caption" sx={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>
                      {totalCorrections} auto-correction{totalCorrections !== 1 ? 's' : ''} applied
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })()}

        {/* Assembly validation results (hierarchical output) */}
        {assemblyValidation && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Hierarchical Output Validation
            </Typography>
            {assemblyValidation.results.map((r) => {
              const isPassed = r.passed;
              const Icon = isPassed ? PassIcon : ErrorIcon;
              const color = isPassed ? '#059669' : '#dc2626';
              return (
                <Box key={r.name} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5, ml: 1 }}>
                  <Icon sx={{ fontSize: 18, color, mt: '2px' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {r.name.replace(/_/g, ' ')}
                      {!isPassed && (
                        <Chip label={`${r.error_count} error(s)`} size="small" color="error" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Typography>
                    {r.errors.length > 0 && r.errors.slice(0, 5).map((err, i) => (
                      <Typography key={i} variant="caption" color="error" component="div" sx={{ ml: 1 }}>
                        {err}
                      </Typography>
                    ))}
                    {r.errors.length > 5 && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ... and {r.errors.length - 5} more
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Per-table results */}
        {validationResults.map((result) => {
          const config = statusConfig[result.status];
          const StatusIcon = config.icon;
          const isExpanded = expandedTables.has(result.table);
          const issueCount = result.issues.filter((i) => i.severity !== 'ok').length;

          return (
            <Box key={result.table} sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.75,
                  px: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: alpha(config.color, 0.2),
                  bgcolor: alpha(config.color, 0.03),
                  cursor: issueCount > 0 ? 'pointer' : 'default',
                }}
                onClick={() => issueCount > 0 && toggleTable(result.table)}
              >
                <StatusIcon sx={{ fontSize: 18, color: config.color }} />
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1, fontSize: 12 }}>
                  {result.table}
                </Typography>
                <Chip
                  label={config.label}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: alpha(config.bg, 0.12),
                    color: config.color,
                  }}
                />
                {issueCount > 0 && (
                  <Typography variant="caption" sx={{ fontSize: 10, color: '#64748b', mx: 0.5 }}>
                    {issueCount}
                  </Typography>
                )}
                {issueCount > 0 && (
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    {isExpanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                )}
              </Box>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ ml: 2, mr: 1, mt: 0.5, mb: 0.5 }}>
                  {result.issues
                    .filter((i) => i.severity !== 'ok')
                    .map((issue, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.75,
                          py: 0.5,
                          px: 1,
                          borderRadius: 0.5,
                          '&:hover': { bgcolor: alpha('#94a3b8', 0.06) },
                        }}
                      >
                        {issue.severity === 'error' ? (
                          <ErrorIcon sx={{ fontSize: 14, color: severityColors.error, mt: 0.25 }} />
                        ) : (
                          <WarnIcon sx={{ fontSize: 14, color: severityColors.warning, mt: 0.25 }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
                            {issue.column}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: '#64748b', lineHeight: 1.4 }}>
                            {issue.message}
                          </Typography>
                        </Box>
                        {issue.source_type && issue.target_type && (
                          <Typography variant="caption" sx={{ fontSize: 9, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                            {issue.source_type} → {issue.target_type}
                          </Typography>
                        )}
                      </Box>
                    ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontSize: 12, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<ExportIcon sx={{ fontSize: 14 }} />}
          onClick={onExportAnyway}
          sx={{
            fontSize: 12,
            textTransform: 'none',
            bgcolor: hasErrors ? '#dc2626' : hasWarnings ? '#d97706' : '#059669',
            '&:hover': { bgcolor: hasErrors ? '#b91c1c' : hasWarnings ? '#b45309' : '#047857' },
          }}
        >
          {hasErrors ? 'Export with Issues' : hasWarnings ? 'Export Anyway' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportValidationDialog;
